import type { FC } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack, xcss, Inline, Box } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import Pressable from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';
import { SpotlightTarget } from '@atlaskit/onboarding';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';

import { FacepileRow, type FacepileRowProps } from './facepileRow/FacepileRow';

export type SpecificAccessFacePileRowProps = {
	facepileRow: FacepileRowProps | undefined;
	isExpanded: boolean;
	onRemoveAllClick: (() => void) | undefined;
	edition: ConfluenceEdition;
};

const i18n = defineMessages({
	specificAccess: {
		id: 'share-and-restrict-dialog.specific-access.heading',
		defaultMessage: 'Specific access',
		description:
			'Section heading for controls that determine which specific users and groups are able to access this content',
	},
	removeAll: {
		id: 'share-and-restrict-dialog.remove-all.button',
		defaultMessage: 'Remove all',
		description: 'button text to info user to remove all specific users from the the current list',
	},
});

const expandedFacepileStyle = xcss({
	// This padding is necessary to make the dialog look just right, and there's no token for 10px
	// @ts-ignore style-maps.partial.tsx(1111, 2): The expected type comes from property 'marginBlockEnd' which is declared here on type 'SafeCSSObject'
	marginBlockEnd: '-10px',
});

const singlePrincipalStyle = xcss({
	marginBlockEnd: 'space.negative.025',
});

const removeAllButtonStyle = xcss({
	color: 'color.text.subtlest',
	textDecorationLine: 'underline',
	font: token('font.body.small'),
	backgroundColor: 'color.background.neutral.subtle',
	paddingBlockStart: 'space.025',
});

const removeAllButtonContainer = xcss({
	color: 'color.text.subtlest',
});

const spotLightContainer = xcss({
	padding: 'space.075',
});

const headingContainer = xcss({
	marginInlineStart: 'space.negative.075',
});

export const SpecificAccessFacePileRow: FC<SpecificAccessFacePileRowProps> = ({
	facepileRow,
	isExpanded,
	onRemoveAllClick,
	edition,
}) => {
	const isFreeEdition = edition === ConfluenceEdition.FREE;

	return (
		<Stack
			space="space.050"
			alignInline="start"
			xcss={
				(facepileRow && isExpanded && expandedFacepileStyle) ||
				(!facepileRow && singlePrincipalStyle)
			}
		>
			<Inline space="space.0" alignBlock="center" xcss={headingContainer}>
				<SpotlightTarget name="specificAccess">
					<Box xcss={spotLightContainer}>
						<Heading size="xxsmall" as="h2">
							<FormattedMessage {...i18n.specificAccess} />
						</Heading>
					</Box>
				</SpotlightTarget>
				{onRemoveAllClick && !isFreeEdition && (
					<Inline xcss={removeAllButtonContainer} alignBlock="start">
						•
						<Pressable xcss={removeAllButtonStyle} onClick={() => onRemoveAllClick()}>
							<FormattedMessage {...i18n.removeAll} />
						</Pressable>
					</Inline>
				)}
			</Inline>
			{facepileRow && (
				<Stack space="space.100" alignInline="start" grow="fill">
					{/* When it's just "You" in the list, don't show FacepileRow */}
					<FacepileRow {...facepileRow} />
				</Stack>
			)}
		</Stack>
	);
};
